<template>
  <div class="historico-versoes">
    <div class="cabecalho-historico">
      <div>
        <div class="title primary--text">Histórico de Versões</div>
        <div class="steps-historico">
          <a class="step-link" @click="voltar">Gestão de taxas</a>
          <span class="step-separador">&gt;</span>
          <a class="step-atual" @click="irParaTopo">Histórico de versões</a>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outline color="primary" class="botao-voltar" @click="voltar">Voltar</v-btn>
    </div>

    <div v-if="versoes.length === 0" class="caption grey--text py-3">
      Nenhuma versão encontrada.
    </div>

    <div v-for="versao in versoes" :key="versao.id" class="hist-item">
      <div class="linha-versao">
        <div class="dados-versao">
          <div class="subheading primary--text">
            Versão {{ versao.numeroVersao }}
            <v-chip v-if="versao.revertida" small color="grey" text-color="white">
              REVERTIDA
            </v-chip>
            <v-chip v-else-if="estaVigente(versao)" small color="green" text-color="white">
              VIGENTE
            </v-chip>
            <v-chip v-else-if="estaAgendada(versao)" small color="orange" text-color="white">
              AGENDADA
            </v-chip>
          </div>
          <div class="caption grey--text">
            Vigência: {{ formatarData(versao.inicioVigencia) }} até
            {{ versao.fimVigencia ? formatarData(versao.fimVigencia) : '—' }}
          </div>
          <div class="caption grey--text">
            Usuário: {{ versao.usuario || '—' }} · Inclusão:
            {{ formatarDataHora(versao.dataInclusao) }}
          </div>
        </div>
        <div class="acoes-versao">
          <v-btn flat color="primary" @click="alternar(versao.id)">Ver alterações</v-btn>
          <v-btn
            flat
            color="error"
            :disabled="!versao.podeReverter || versao.revertida || revertendo"
            @click="abrirJustificativa(versao)"
          >
            Reverter
          </v-btn>
        </div>
      </div>

      <div v-if="versao.motivoBloqueioReversao" class="bloqueio-reversao caption">
        <v-icon small color="orange darken-2">info</v-icon>
        {{ versao.motivoBloqueioReversao }}
      </div>

      <div v-if="expandidas.indexOf(versao.id) !== -1">
        <div v-if="!versao.alteracoes" class="caption grey--text py-2">
          Sem detalhamento por taxa (versão anterior ao módulo de auditoria).
        </div>
        <div v-else-if="versao.alteracoes.length === 0" class="caption grey--text py-2">
          Nenhuma alteração registrada.
        </div>
        <table v-else class="tabela-diff">
          <thead>
            <tr>
              <th>Cobertura</th>
              <th>Faixa IS / Atributo / Período</th>
              <th>Categoria</th>
              <th>Taxa Anterior</th>
              <th>Taxa Nova</th>
              <th>Origem</th>
              <th>Data de Alteração</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(alteracao, idx) in versao.alteracoes" :key="idx">
              <td>{{ alteracao.cobertura }}</td>
              <td>{{ descreverFaixa(alteracao) }}</td>
              <td>{{ alteracao.categoria }}</td>
              <td>{{ formatarValor(alteracao, alteracao.fatorAnterior) }}</td>
              <td>{{ formatarValor(alteracao, alteracao.fatorNovo) }}</td>
              <td>{{ alteracao.origem }}</td>
              <td>{{ formatarDataHora(alteracao.dataAlteracao) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <v-divider class="my-2"></v-divider>
    </div>

    <v-dialog v-model="dialogJustificativa" max-width="560" persistent>
      <v-card>
        <v-card-title class="primary white--text">Justificar Reversão</v-card-title>
        <v-card-text>
          <div class="mb-2">
            Será revertida a
            <strong>versão {{ versaoAReverter && versaoAReverter.numeroVersao }}</strong>
            <span v-if="versaoAReverter">
              (vigência a partir de {{ formatarData(versaoAReverter.inicioVigencia) }})</span
            >.
          </div>
          <div class="caption grey--text mb-3">
            {{ textoEfeitoReversao }}
          </div>
          <v-textarea
            v-model="motivo"
            label="Motivo da reversão"
            :rules="[regraMotivo]"
            :counter="1000"
            rows="4"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="revertendo" @click="fecharJustificativa">Cancelar</v-btn>
          <v-btn
            color="error"
            :disabled="!motivoValido || revertendo"
            :loading="revertendo"
            @click="confirmarReversao"
          >
            Confirmar Reversão
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import usuarioHelper from '@/helpers/usuario-logado.js';
import gestaoTaxasHelper from './gestao-taxas-helper';

const MOTIVO_MAX = 1000;

export default {
  name: 'HistoricoVersoes',
  props: {
    versoes: { type: Array, default: () => [] }
  },
  data() {
    return {
      expandidas: [],
      dialogJustificativa: false,
      versaoAReverter: null,
      motivo: '',
      revertendo: false
    };
  },
  computed: {
    motivoValido() {
      const texto = (this.motivo || '').trim();
      return texto.length > 0 && texto.length <= MOTIVO_MAX;
    },
    textoEfeitoReversao() {
      if (this.versaoAReverter && this.estaVigente(this.versaoAReverter)) {
        return (
          'Esta versão está vigente: ela será encerrada hoje e marcada como revertida, e a ' +
          'versão anterior voltará a vigorar. Nenhuma versão é excluída.'
        );
      }
      return (
        'As taxas alteradas por essa versão voltarão ao valor que tinham antes dela, em uma ' +
        'nova versão vigente a partir de hoje. Nenhuma versão é excluída.'
      );
    }
  },
  methods: {
    voltar() {
      this.$emit('voltar');
    },
    irParaTopo() {
      window.scrollTo(0, 0);
    },
    regraMotivo(valor) {
      const texto = (valor || '').trim();
      if (!texto) return 'Informe o motivo da reversão.';
      if (texto.length > MOTIVO_MAX) return `Máximo de ${MOTIVO_MAX} caracteres.`;
      return true;
    },
    abrirJustificativa(versao) {
      this.versaoAReverter = versao;
      this.motivo = '';
      this.dialogJustificativa = true;
    },
    fecharJustificativa() {
      this.dialogJustificativa = false;
      this.versaoAReverter = null;
      this.motivo = '';
    },
    confirmarReversao() {
      if (!this.motivoValido || !this.versaoAReverter) return;
      this.revertendo = true;
      const cabecalho = {
        headers: { Authorization: `Bearer ${usuarioHelper.USUARIO_LOGADO().token}` }
      };
      axios
        .post(
          `${process.env.URL_GESTAOTAXASMS}/v1/versoes/${this.versaoAReverter.id}/reverter`,
          { motivo: this.motivo.trim() },
          cabecalho
        )
        .then(response => {
          this.revertendo = false;
          this.fecharJustificativa();
          this.$emit('revertido', response.data);
        })
        .catch(erro => {
          this.revertendo = false;
          const mensagem =
            erro.response && erro.response.data && erro.response.data.message
              ? erro.response.data.message
              : 'Não foi possível reverter a versão.';
          this.$emit('erro', mensagem);
        });
    },
    hoje() {
      const agora = new Date();
      const mes = String(agora.getMonth() + 1).padStart(2, '0');
      const dia = String(agora.getDate()).padStart(2, '0');
      return `${agora.getFullYear()}-${mes}-${dia}`;
    },
    estaVigente(versao) {
      return !!versao.vigente;
    },
    estaAgendada(versao) {
      const inicio = String(versao.inicioVigencia || '').substr(0, 10);
      return !!inicio && inicio > this.hoje();
    },
    alternar(id) {
      const indice = this.expandidas.indexOf(id);
      if (indice === -1) this.expandidas.push(id);
      else this.expandidas.splice(indice, 1);
    },
    formatarData(iso) {
      if (!iso) return '—';
      const [ano, mes, dia] = String(iso).substr(0, 10).split('-');
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(iso) {
      if (!iso) return '—';
      const data = new Date(iso);
      const p = n => (n < 10 ? `0${n}` : `${n}`);
      return `${p(data.getDate())}/${p(data.getMonth() + 1)}/${data.getFullYear()} ` +
        `${p(data.getHours())}:${p(data.getMinutes())}`;
    },
    descreverFaixa(alteracao) {
      return gestaoTaxasHelper.descreverFaixaAlteracao(alteracao);
    },
    formatarValor(alteracao, valor) {
      return gestaoTaxasHelper.formatarValorAlteracao(alteracao, valor);
    }
  }
};
</script>

<style scoped>
.cabecalho-historico {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}
.botao-voltar {
  margin: 0;
  min-width: 112px;
}
.steps-historico {
  font-size: 12px;
  margin-top: 2px;
}
.step-link {
  color: #184170;
  font-weight: 600;
  cursor: pointer;
}
.step-link:hover {
  text-decoration: underline;
}
.step-separador {
  color: #184170;
  margin: 0 6px 0 4px;
}
.step-atual {
  color: #9e9e9e;
  cursor: pointer;
}
.hist-item {
  padding: 8px 0;
}
.linha-versao {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.dados-versao {
  flex: 0 0 460px;
  max-width: 100%;
}
.acoes-versao {
  white-space: nowrap;
}
.tabela-diff {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
}
.tabela-diff th {
  background: #f4f4f4;
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}
.tabela-diff td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
}
.bloqueio-reversao {
  background: #fff3cd;
  border-left: 3px solid #f5a623;
  border-radius: 2px;
  color: #7a5b00;
  margin-top: 4px;
  padding: 6px 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/gestao-taxas/HistoricoVersoes.vue